import {Component, Input, Output, EventEmitter} from '@angular/core';
import {
  EventClassResponse,
  EventResponse,
  EventServiceResponse
} from '../../../../../../../../@shared/models/response.module';
import {EventsService} from '../../../../../../../../@shared/services/events-service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {
  _AQH_EVE_10,
  _AQH_EVE_13, _AQH_EVE_14,
  _AQH_EVE_9,
  AQH_EVE_11,
  AQH_EVE_6
} from '../../../../../../../../@shared/models/input.module';
import {EVENT_CLASS_STATUSES, formatTimeDMYHHMM} from '../../../../../../../../@core/utils/utils.service';
import {AddEventService} from './components/add-event-service/add-event-service';
import {EditEventService} from './components/edit-event-service/edit-event-service';
import {DeleteModal} from '../../../../../../../../@shared/components/delete-modal/delete-modal';
import {ViewServiceRegistrations} from './components/view-service-registrations/view-service-registrations';
import {MembershipPricingModal} from '../manage-event-classes/components/membership-pricing-modal/membership-pricing-modal';
import {TranslateService} from '@ngx-translate/core';
import {ToastService} from '../../../../../../../../@core/services/toast.service';


@Component({
  selector: 'app-manage-event-service',
  standalone: false,
  templateUrl: './manage-event-service.html',
  styleUrl: './manage-event-service.css'
})
export class ManageEventService {
  @Input() event: EventResponse
  @Output() dataChanged = new EventEmitter<void>();
  eventServices: EventServiceResponse[]
  loading: boolean = false;

  constructor(private eventService: EventsService,
              private modalService: NgbModal,
              private translateService: TranslateService,
              private toastService: ToastService) {
  }

  ngOnInit() {
    this.getEventServices()
  }

  getEventServices() {
    this.loading = true;
    let params: AQH_EVE_11 = {
      event_id: this.event.event_id
    }
    this.eventService.getEventServices(params).subscribe({
      next: (data) => {
        this.eventServices = data;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  openAddEventServiceModal() {
    let modalRef = this.modalService.open(AddEventService, {centered: true, size: 'lg'});
    modalRef.componentInstance.event = this.event;
    modalRef.result.then((result) => {
      if (result) {
        this.getEventServices();
        this.dataChanged.emit();
      }
    }).catch((error) => {
      // Handle dismissal if needed
    });
  }


  openEditEventServiceModal(service: EventServiceResponse) {
    let modalRef = this.modalService.open(EditEventService, {centered: true, size: 'lg'});
    modalRef.componentInstance.service = service;
    modalRef.componentInstance.event = this.event;
    modalRef.result.then((result) => {
      if (result) {
        this.getEventServices();
        this.dataChanged.emit();
      }
    })
  }

  openViewRegistrationsModal(service: EventServiceResponse) {
    let modalRef = this.modalService.open(ViewServiceRegistrations, { size: 'xl', centered: true });
    modalRef.componentInstance.eventService = service;
    modalRef.componentInstance.companyId = this.event.company_id;
    modalRef.componentInstance.company = this.event.company;

    modalRef.result.then((updated) => {
      if (updated) {
        // Refresh the event services list to show updated data
        this.getEventServices();
      }
    }, () => {
      // Modal dismissed without changes
    });
  }

  deleteEventService(service: EventServiceResponse) {
    let modalRef = this.modalService.open(DeleteModal, {centered: true, size: 'lg'});
    modalRef.componentInstance.description = this.translateService.instant('manageEvent.services.deleteConfirm');
    modalRef.result.then((res) => {
      if (res) {
        let params: _AQH_EVE_14 = {
          event_service_id: service.event_service_id
        }
        this.eventService.deleteEventService(params).subscribe({
          next: () => {
            this.toastService.success(this.translateService.instant('manageEvent.services.deleteSuccess'));
            this.getEventServices();
            this.dataChanged.emit();
          },
          error: () => {
            this.toastService.error(this.translateService.instant('manageEvent.services.deleteError'));
          }
        });
      }
    })
  }

  getTotalRegistrations(service: EventServiceResponse): number {
    if (!service.event_service_registrations || service.event_service_registrations.length === 0) {
      return 0;
    }
    return service.event_service_registrations.reduce((total, registration) => {
      return total + (registration.num_registrations || 0);
    }, 0);
  }

  /**
   * Calculate net amount after platform + payment fees
   * Stripe: 2.4% + 2.00 NOK, Platform: 2%
   * Net = price × 0.956 - 2.00 per transaction
   */
  calculateNetAmount(grossAmount: number, transactionCount: number): number {
    if (!grossAmount || grossAmount <= 0) return 0;
    const net = grossAmount * 0.956 - (2 * transactionCount);
    return Math.max(0, Math.round(net * 100) / 100);
  }

  getServiceNetRevenue(service: EventServiceResponse): number {
    const totalRegs = this.getTotalRegistrations(service);
    const transactionCount = service.event_service_registrations?.length || 0;
    const grossRevenue = (service.price || 0) * totalRegs;
    return this.calculateNetAmount(grossRevenue, transactionCount);
  }

  openMembershipPricingModal(service: EventServiceResponse) {
    let modalRef = this.modalService.open(MembershipPricingModal, { size: 'lg', centered: true });
    modalRef.componentInstance.itemType = 'service';
    modalRef.componentInstance.itemId = service.event_service_id;
    modalRef.componentInstance.itemName = service.event_service_name;
    modalRef.componentInstance.basePrice = service.price || 0;
    modalRef.componentInstance.useCents = false;
    modalRef.componentInstance.currency = this.event?.default_currency || service.price_currency || 'NOK';

    modalRef.result.then((result) => {
      if (result) {
        this.getEventServices();
      }
    }).catch(() => {});
  }

  protected readonly formatTimeDMYHHMM = formatTimeDMYHHMM;
  protected readonly EVENT_CLASS_STATUSES = EVENT_CLASS_STATUSES;
}
